import React, { useState, useMemo, useEffect } from "react";
import { useDispatch } from "react-redux";

import { RoleTypeExtension } from "../../constants";
import { useDisableBodyScroll } from "../../hooks/useDisableBodyScroll";
import { accountUsersActions } from "../../reducers/accountUsers";
import type { AppDispatch } from "../../store";
import type {
  GeneralUserData,
  AccountData,
} from "../../types/generalUserManage";
import { constrainCreditLimit } from "../../utils/creditLimitUtils";

import CreditLimitAdjustButtons from "./CreditLimitAdjustButtons";
import UserDashboardBox from "./UserDashboardBox";

interface LimitSettingModalProps {
  onClose: () => void;
  accountData: AccountData;
  users: GeneralUserData[];
}

interface UserLimitState {
  user_id: number;
  checked: boolean;
  limit: string; // 入力値は文字列として管理
}

const LimitSettingModal: React.FC<LimitSettingModalProps> = ({
  onClose,
  accountData,
  users,
}) => {
  const dispatch = useDispatch<AppDispatch>();

  // モーダル表示時に背景のスクロールを無効化
  useDisableBodyScroll();

  // ユーザータイプフィルター
  const [showGeneralUsers, setShowGeneralUsers] = useState(true);
  const [showAdmins, setShowAdmins] = useState(false);

  // フィルタリングされたユーザーリスト
  const filteredUsers = useMemo(() => {
    return users.filter((user) => {
      return (
        (showGeneralUsers && RoleTypeExtension.isGeneral(user.role_type)) ||
        (showAdmins && RoleTypeExtension.isAdmin(user.role_type))
      );
    });
  }, [users, showGeneralUsers, showAdmins]);

  // 全選択チェックボックス
  const [selectAll, setSelectAll] = useState(true);

  // 各ユーザーのチェック状態と上限値
  const [userLimits, setUserLimits] = useState<UserLimitState[]>(
    users.map((user) => ({
      user_id: user.user_id,
      checked: true,
      limit: user.credit_limit?.toString() || "",
    })),
  );

  // 一括設定用の入力値
  const [bulkLimitValue, setBulkLimitValue] = useState("");

  // フィルターが変更されたときに全選択状態を更新
  useEffect(() => {
    const filteredUserIds = new Set(filteredUsers.map((u) => u.user_id));
    const allChecked = userLimits
      .filter((user) => filteredUserIds.has(user.user_id))
      .every((user) => user.checked);
    setSelectAll(allChecked);
  }, [filteredUsers, userLimits]);

  // 割振済みクレジットの計算
  const assignedCredits = useMemo(() => {
    return userLimits.reduce((sum, userLimit) => {
      const limit = userLimit.limit === "" ? 0 : parseInt(userLimit.limit, 10);
      return sum + (isNaN(limit) ? 0 : limit);
    }, 0);
  }, [userLimits]);

  // 割振率の計算
  const assignmentRate = useMemo(() => {
    if (accountData.credit_limit === 0) return 0;
    return Math.ceil((assignedCredits / accountData.credit_limit) * 100);
  }, [assignedCredits, accountData.credit_limit]);

  // 全選択のトグル
  const handleSelectAllChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { checked } = e.target;
    setSelectAll(checked);
    // filteredUsers に含まれるユーザーのみチェック状態を変更
    const filteredUserIds = new Set(filteredUsers.map((u) => u.user_id));
    setUserLimits((prev) =>
      prev.map((user) =>
        filteredUserIds.has(user.user_id) ? { ...user, checked } : user,
      ),
    );
  };

  // 個別チェックボックスのトグル
  const handleUserCheckChange = (userId: number) => {
    setUserLimits((prev) => {
      const updated = prev.map((user) =>
        user.user_id === userId ? { ...user, checked: !user.checked } : user,
      );
      // filteredUsers に含まれるユーザーのみを対象に全てチェックされているか確認
      const filteredUserIds = new Set(filteredUsers.map((u) => u.user_id));
      const allChecked = updated
        .filter((user) => filteredUserIds.has(user.user_id))
        .every((user) => user.checked);
      setSelectAll(allChecked);
      return updated;
    });
  };

  // 個別上限値の変更
  const handleLimitChange = (userId: number, value: string) => {
    const constrainedValue = constrainCreditLimit(
      value,
      accountData.credit_limit,
    );
    setUserLimits((prev) =>
      prev.map((user) =>
        user.user_id === userId ? { ...user, limit: constrainedValue } : user,
      ),
    );
  };

  // 一括設定ボタン
  const handleBulkSet = () => {
    const filteredUserIds = new Set(filteredUsers.map((u) => u.user_id));
    setUserLimits((prev) =>
      prev.map((user) =>
        user.checked && filteredUserIds.has(user.user_id)
          ? { ...user, limit: bulkLimitValue }
          : user,
      ),
    );
  };

  // 確定ボタン
  const handleConfirm = async () => {
    // 変更があったユーザーのみを抽出
    const userLimitsPayload = userLimits
      .filter((userLimit) => {
        const originalUser = users.find((u) => u.user_id === userLimit.user_id);
        if (!originalUser) return false;

        const newLimit =
          userLimit.limit === "" ? null : parseInt(userLimit.limit, 10);

        // 値が変更されている場合のみ含める
        return originalUser.credit_limit !== newLimit;
      })
      .map((userLimit) => ({
        user_id: userLimit.user_id,
        credit_limit:
          userLimit.limit === "" ? null : parseInt(userLimit.limit, 10),
      }));

    // 変更がない場合はAPIを呼ばずに閉じる
    if (userLimitsPayload.length === 0) {
      onClose();
      return;
    }

    try {
      await dispatch(
        accountUsersActions.bulkUpdateUserLimits({
          account_id: accountData.id,
          user_limits: userLimitsPayload,
        }),
      ).unwrap();

      // 成功したらモーダルを閉じる
      onClose();
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("上限更新エラー:", error);
    }
  };

  return (
    <div className="fixed inset-0 z-100 flex items-center justify-center">
      {/* 背景オーバーレイ */}
      <div
        className="absolute inset-0"
        style={{ backgroundColor: "rgba(0, 0, 0, 0.5)" }}
      />

      {/* モーダル本体 */}
      <div
        className="relative bg-white rounded-lg shadow-lg overflow-hidden"
        style={{ maxHeight: "90vh" }}
      >
        {/* タイトルバー */}
        <div className="bg-[#5571ad] text-white px-6 py-3">
          <h3 className="text-2xl font-bold">上限一括設定</h3>
        </div>

        {/* スクロール可能なコンテンツエリア */}
        <div
          className="overflow-y-auto"
          style={{ maxHeight: "calc(90vh - 140px)" }}
        >
          <div className="p-6">
            {/* クレジット割振状況 */}
            <h4 className="text-lg font-bold mb-4">クレジット割振状況</h4>
            <div className="grid grid-cols-3 gap-4 mb-6">
              <UserDashboardBox
                title="クレジット上限"
                mainText={accountData.credit_limit.toLocaleString()}
                subText="クレジット"
                mainTextColor="#2563eb"
              />
              <UserDashboardBox
                title="割振済みクレジット"
                mainText={assignedCredits.toLocaleString()}
                subText="クレジット"
                mainTextColor="#16a34a"
              />
              <UserDashboardBox
                title="割振率"
                mainText={`${assignmentRate}%`}
                subText={
                  assignmentRate > 100 ? "100%を超える設定はできません" : ""
                }
                mainTextColor={assignmentRate > 100 ? "#ef4444" : "#f97316"}
                subTextColor="#ef4444"
              />
            </div>

            {/* ユーザリスト */}
            <div className="flex justify-between items-center mb-4">
              <div className="flex items-center gap-4">
                <h4 className="text-lg font-bold">ユーザーリスト</h4>
                <div className="flex items-center gap-3 text-sm">
                  <label
                    htmlFor="filter-general"
                    className="flex items-center gap-1 cursor-pointer"
                  >
                    <input
                      id="filter-general"
                      type="checkbox"
                      checked={showGeneralUsers}
                      onChange={(e) => setShowGeneralUsers(e.target.checked)}
                      className="cursor-pointer"
                    />
                    <span>一般</span>
                  </label>
                  <label
                    htmlFor="filter-admin"
                    className="flex items-center gap-1 cursor-pointer"
                  >
                    <input
                      id="filter-admin"
                      type="checkbox"
                      checked={showAdmins}
                      onChange={(e) => setShowAdmins(e.target.checked)}
                      className="cursor-pointer"
                    />
                    <span>管理者</span>
                  </label>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  value={bulkLimitValue}
                  onChange={(e) =>
                    setBulkLimitValue(
                      constrainCreditLimit(
                        e.target.value,
                        accountData.credit_limit,
                      ),
                    )
                  }
                  placeholder="上限値を入力"
                  className="px-3 py-2 border border-gray-300 rounded-md w-48"
                />
                <CreditLimitAdjustButtons
                  currentValue={bulkLimitValue}
                  onChange={(value) =>
                    setBulkLimitValue(
                      constrainCreditLimit(value, accountData.credit_limit),
                    )
                  }
                />
                <button
                  type="button"
                  onClick={handleBulkSet}
                  className="px-4 py-2 text-sm font-medium text-white bg-orange-500 rounded-md hover:bg-orange-400"
                >
                  一括設定
                </button>
              </div>
            </div>
            <div className="border border-gray-300">
              <div className="overflow-y-auto" style={{ maxHeight: "300px" }}>
                <table className="w-full border-collapse">
                  <thead
                    className="bg-[#5571ad]"
                    style={{ position: "sticky", top: 0, zIndex: 10 }}
                  >
                    <tr>
                      <th
                        className="px-3 py-2 text-center text-white border border-[#889bc5]"
                        style={{ width: "80px" }}
                      >
                        <div className="flex flex-col items-center gap-1">
                          <span className="text-xs">一括対象</span>
                          <input
                            type="checkbox"
                            checked={selectAll}
                            onChange={handleSelectAllChange}
                          />
                        </div>
                      </th>
                      <th
                        className="px-3 py-2 text-center text-white border border-[#889bc5]"
                        style={{ width: "100px" }}
                      >
                        No
                      </th>
                      <th
                        className="px-3 py-2 text-center text-white border border-[#889bc5]"
                        style={{ width: "200px" }}
                      >
                        ユーザ名
                      </th>
                      <th
                        className="px-3 py-2 text-center text-white border border-[#889bc5]"
                        style={{ width: "200px" }}
                      >
                        ログインID
                      </th>
                      <th
                        className="px-3 py-2 text-center text-white border border-[#889bc5]"
                        style={{ width: "200px" }}
                      >
                        個別上限(今月)
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white">
                    {filteredUsers.map((user) => {
                      const userLimit = userLimits.find(
                        (ul) => ul.user_id === user.user_id,
                      );
                      return (
                        <tr key={user.user_id} className="hover:bg-gray-50">
                          <td
                            className="p-2 border border-gray-300 text-center"
                            style={{ width: "80px" }}
                          >
                            <input
                              type="checkbox"
                              checked={userLimit?.checked || false}
                              onChange={() =>
                                handleUserCheckChange(user.user_id)
                              }
                            />
                          </td>
                          <td
                            className="p-2 border border-gray-300 text-center"
                            style={{ width: "100px" }}
                          >
                            {user.employee_id
                              ? String(user.employee_id).padStart(5, "0")
                              : "-"}
                          </td>
                          <td
                            className="p-2 border border-gray-300"
                            style={{ width: "200px" }}
                          >
                            {user.full_name || "-"}
                          </td>
                          <td
                            className="p-2 border border-gray-300"
                            style={{ width: "200px" }}
                          >
                            {user.user_name}
                          </td>
                          <td
                            className="p-2 border border-gray-300"
                            style={{ width: "200px" }}
                          >
                            <div className="flex items-center gap-1">
                              <input
                                type="number"
                                value={userLimit?.limit || ""}
                                onChange={(e) =>
                                  handleLimitChange(
                                    user.user_id,
                                    e.target.value,
                                  )
                                }
                                className="px-2 py-1 border mr-1 border-gray-300 rounded"
                                style={{ width: "80px" }}
                              />
                              <CreditLimitAdjustButtons
                                currentValue={userLimit?.limit || ""}
                                onChange={(value) =>
                                  handleLimitChange(user.user_id, value)
                                }
                              />
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>

        {/* ボトムバー */}
        <div className="flex justify-end gap-3 px-6 py-3 border-t bg-white">
          <button
            type="button"
            onClick={onClose}
            className="px-6 py-2 text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
          >
            キャンセル
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={assignmentRate > 100}
            className={`px-6 py-2 text-white rounded-md ${
              assignmentRate > 100
                ? "bg-gray-400 cursor-not-allowed"
                : "bg-blue-600 hover:bg-blue-700"
            }`}
          >
            確定
          </button>
        </div>
      </div>
    </div>
  );
};

export default LimitSettingModal;
